import { decryptPayload } from "./utils";

type PostcardLike = {
  id?: number | string;
  status?: string | null;
} | null | undefined;

type MailingAddressLike = {
  id?: number | string;
  property_id?: number | string | null;
  is_verified?: number | boolean;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  postcard?: PostcardLike;
} | null | undefined;

export function propertyNeedsVerification(
  property: Record<string, unknown> | null | undefined,
): boolean {
  return Number(property?.is_verified) === 0;
}

export function resolvePropertyUserId(
  property: Record<string, unknown> | null | undefined,
  userProfilePayload?: { data?: Record<string, unknown> } | null,
): string | number {
  if (property?.owner_id != null && String(property.owner_id).trim() !== "") {
    return property.owner_id as string | number;
  }

  const data = userProfilePayload?.data;
  if (data?.user_id != null && String(data.user_id).trim() !== "") {
    return data.user_id as string | number;
  }
  if (data?.id != null && String(data.id).trim() !== "") {
    return data.id as string | number;
  }

  try {
    const raw = localStorage.getItem("user_id");
    if (raw) {
      const parsed = decryptPayload(raw);
      if (parsed != null && String(parsed).trim() !== "") {
        return parsed as string | number;
      }
    }
  } catch {
    // ignore storage errors
  }

  return "";
}

export function addressHasSentPostcard(
  address: Record<string, unknown> | null | undefined,
): boolean {
  const postcards = Array.isArray(address?.postcards)
    ? (address.postcards as PostcardLike[])
    : [];
  if (postcards.some((postcard) => postcard?.id != null && postcard.id !== "")) {
    return true;
  }

  const postcard = address?.postcard as PostcardLike;
  if (postcard?.id != null && postcard.id !== "") {
    return true;
  }
  const status = String(postcard?.status ?? "").trim();
  return status.length > 0 && status !== "cancelled";
}

export function isMailingAddressVerifiedForDisplay(
  address: Record<string, unknown> | null | undefined,
): boolean {
  if (address?.is_mailing_verified != null) {
    return Boolean(address.is_mailing_verified);
  }

  return Number(address?.is_verified) === 1;
}

export function addressNeedsPostcardAttention(
  address: Record<string, unknown> | null | undefined,
): boolean {
  if (isMailingAddressVerifiedForDisplay(address)) {
    return false;
  }
  return addressHasSentPostcard(address);
}

export function customerHasPendingPostcardAttention(
  addresses: unknown[] | null | undefined,
): boolean {
  if (!Array.isArray(addresses)) {
    return false;
  }
  return addresses.some((row) =>
    addressNeedsPostcardAttention(row as Record<string, unknown>),
  );
}

export function shouldShowStanupAttentionModal({
  addresses = [],
}: { addresses?: unknown[] } = {}): boolean {
  return customerHasPendingPostcardAttention(addresses);
}

export function propertyHasSentPostcard(
  property: Record<string, unknown> | null | undefined,
): boolean {
  const mailing = property?.user_mailing_address as MailingAddressLike;
  return addressHasSentPostcard(mailing as Record<string, unknown>);
}

export function showPropertyPostcardTab(
  property: Record<string, unknown> | null | undefined,
): boolean {
  return propertyHasSentPostcard(property);
}

export function buildPropertyEpAwareVerifyPayload(
  property: Record<string, unknown>,
  userId: number | string,
): Record<string, unknown> {
  const jsonResponse = (property.json_response || {}) as Record<string, unknown>;

  return {
    property_id: property.property_id,
    prop_id: property.id,
    from_resume: true,
    user_id: userId,
    state_code: property.mm_fips_state_code ?? jsonResponse.mm_fips_state_code,
    address: property.address,
    city: property.city,
    state: property.state,
    zip: property.zip,
    street_number: property.street_number,
    site_route: property.site_route,
    latitude: property.latitude,
    longitude: property.longitude,
    country: property.country ?? "US",
    answers: { ep_aware: "verified" },
    step: 2,
    step_key: "VERIFY_VIA_EPAWARE",
  };
}

export function findMailingAddressForProperty(
  property: Record<string, unknown> | null | undefined,
  addresses: unknown[] | null | undefined,
): Record<string, unknown> | null {
  if (!property || !Array.isArray(addresses) || addresses.length === 0) {
    return null;
  }

  const propertyId = property.id;
  const mailingId =
    property.mailing_address_id ??
    (property.user_mailing_address as MailingAddressLike)?.id;

  const match = addresses.find((row) => {
    if (!row || typeof row !== "object") {
      return false;
    }
    const entry = row as Record<string, unknown>;
    if (mailingId != null && String(entry.id) === String(mailingId)) {
      return true;
    }
    if (propertyId != null && String(entry.property_id) === String(propertyId)) {
      return true;
    }
    const linkedProperty = entry.property as Record<string, unknown> | undefined;
    if (
      propertyId != null &&
      linkedProperty?.id != null &&
      String(linkedProperty.id) === String(propertyId)
    ) {
      return true;
    }
    return false;
  });

  return (match as Record<string, unknown>) || null;
}

export const POSTCARD_TRACKING_STEPS = [
  { key: "received", label: "Received" },
  { key: "producing", label: "Producing" },
  { key: "handed_over", label: "Handed over" },
  { key: "in_transit", label: "In transit" },
  { key: "local_delivery", label: "Local delivery" },
  { key: "delivered", label: "Delivered" },
] as const;

function postcardTrackingScore(
  postcard: Record<string, unknown> | null | undefined,
): number {
  if (!postcard) {
    return -1;
  }
  const tracking = postcard.tracking as Record<string, unknown> | null | undefined;
  if (!tracking || typeof tracking !== "object") {
    return postcard.id != null || postcard.status ? 0 : -1;
  }

  let score = 0;
  for (const step of POSTCARD_TRACKING_STEPS) {
    const value = tracking[step.key];
    if (value === true || (typeof value === "string" && value.trim() !== "")) {
      score += 1;
    }
  }
  return score;
}

/**
 * Prefer the postcard payload that includes the most complete Stannp tracking.
 */
export function pickRichestPostcard(
  ...sources: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> | null {
  const candidates: Record<string, unknown>[] = [];

  for (const source of sources) {
    if (!source) {
      continue;
    }
    if (Array.isArray(source.postcards)) {
      for (const postcard of source.postcards) {
        if (postcard && typeof postcard === "object") {
          candidates.push(postcard as Record<string, unknown>);
        }
      }
    }
    if (source.postcard && typeof source.postcard === "object") {
      candidates.push(source.postcard as Record<string, unknown>);
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((best, current) =>
    postcardTrackingScore(current) > postcardTrackingScore(best)
      ? current
      : best,
  );
}

/**
 * Merge property-embedded mailing address with customer/address API row so the
 * Postcard tab can show mailing verification + Stannp tracking consistently.
 */
export function resolveMailingAddressForPropertyDisplay(
  property: Record<string, unknown> | null | undefined,
  addresses: unknown[] | null | undefined,
): Record<string, unknown> | null {
  const fromApi = findMailingAddressForProperty(property, addresses);
  const fromProperty =
    (property?.user_mailing_address as Record<string, unknown> | null) || null;

  if (!fromApi && !fromProperty) {
    return null;
  }
  if (!fromApi) {
    return fromProperty;
  }
  if (!fromProperty) {
    return fromApi;
  }

  const postcard = pickRichestPostcard(fromApi, fromProperty);
  return {
    ...fromProperty,
    ...fromApi,
    postcard,
    postcards:
      Array.isArray(fromApi.postcards) && fromApi.postcards.length
        ? fromApi.postcards
        : Array.isArray(fromProperty.postcards) && fromProperty.postcards.length
          ? fromProperty.postcards
          : postcard
            ? [postcard]
            : [],
  };
}

export function formatMailingAddressLine(
  mailing: Record<string, unknown> | null | undefined,
): string {
  if (!mailing) {
    return "";
  }
  return [mailing.address, mailing.city, mailing.state, mailing.zip]
    .filter(Boolean)
    .join(", ");
}

export function getPostcardActiveStep(
  tracking: Record<string, unknown> | null | undefined,
): number {
  if (!tracking) {
    return -1;
  }
  if (tracking.delivered) return 5;
  if (tracking.local_delivery) return 4;
  if (tracking.in_transit) return 3;
  if (tracking.handed_over) return 2;
  if (tracking.producing) return 1;
  if (tracking.received) return 0;
  return -1;
}
